import { useMemo, useState } from "react";
import toast from "react-hot-toast";

import { ItemType } from "@/api";
import { Dropdown, PageLoader } from "@/components";
import { Header } from "@/app/modules/Navigation";
import { cn, getTabIconFilename } from "@/utils";
import {
  useAppProperties,
  useCollections,
  useMainTabs,
  usePlaylists,
  useVideos,
} from "@/hooks";
import { DRAWER_REORDER_PLAYLISTS, TEMPLATE_PLAYLISTS } from "@/const.ts";
import { Delete, Edit, Ellipsis, List } from "@/icons";
import { useResponsiveness, useTranslate } from "@/providers/context";

import { useDeletePlaylist } from "./hooks/useDeletePlaylist.tsx";
import { UpdatePlaylistModal } from "./components/UpdatePlaylistModal.tsx";
import { ReorderPlaylistsDrawer } from "./components/ReorderPlaylistsDrawer.tsx";
import { PlaylistSlider } from "./components/PlaylistSlider.tsx";

interface PlaylistItem {
  ChildId: string;
  Type: ItemType;
  Position: number;
}

export const CustomPlaylists = () => {
  const [isModalOpen, setModalOpen] = useState(false);
  const { data } = usePlaylists();
  const deletePlaylist = useDeletePlaylist();
  const { mainTabs } = useMainTabs();
  const { translate } = useTranslate();
  const [updatePlaylistId, setUpdatePlaylistId] = useState<number | undefined>(
    undefined,
  );
  const { setOpenDrawer, drawerMargin, transitionStyle } = useResponsiveness();
  const { isDarkMode } = useAppProperties();

  const { existingPlaylistTab } = useMemo(() => {
    const existingPlaylistTab = mainTabs?.find(
      (t) => t.TemplateId === TEMPLATE_PLAYLISTS,
    );
    return { existingPlaylistTab };
  }, [mainTabs]);

  const { itemsByPlaylist, videoIds, collectionIds } = useMemo(() => {
    // All videos and collections that are playlisted
    const videoIds: number[] = [];
    const collectionIds: number[] = [];
    const itemsByPlaylist: Record<number, PlaylistItem[]> = {};

    for (const playlist of data?.Playlists ?? []) {
      const items: PlaylistItem[] = [];
      playlist?.Items.forEach((item) => {
        if (!item.ItemId) {
          return;
        }
        items.push({
          ChildId: item.ItemId.toString(),
          Type: item.ItemType,
          Position: item.Position,
        });

        if (item.ItemType === "video") {
          videoIds.push(item.ItemId);
        } else if (item.ItemType === "collection") {
          collectionIds.push(item.ItemId);
        }
      });
      items.sort((a, b) => a.Position - b.Position);

      itemsByPlaylist[playlist.PlaylistId] = items;
    }

    return { itemsByPlaylist, videoIds, collectionIds };
  }, [data?.Playlists]);

  const { videoById } = useVideos({ ids: videoIds });
  const { collectionById } = useCollections({ ids: collectionIds });
  const noPlaylists =
    !!data?.Playlists && videoIds.length === 0 && collectionIds.length === 0;
  const contentNotLoaded =
    videoById === undefined ||
    collectionById === undefined ||
    data === undefined ||
    itemsByPlaylist === undefined;

  if (contentNotLoaded && !noPlaylists) {
    return <PageLoader />;
  }

  return (
    <div
      className="h-full px-7"
      style={{ marginRight: drawerMargin, transition: transitionStyle }}
    >
      <Header
        content={
          <div className="p1-bold">
            {existingPlaylistTab?.Name ?? translate("My Playlists")}
          </div>
        }
        rightContent={
          <Dropdown
            options={[
              {
                text: translate("Reorder Playlists"),
                icon: <List className="h-4 w-4" />,
                onClick: () => setOpenDrawer(DRAWER_REORDER_PLAYLISTS),
              },
            ]}
            contentProps={{ align: "end" }}
          >
            <Ellipsis className="focus-visible:outline-item-secondary h-6 w-6" />
          </Dropdown>
        }
      />
      {contentNotLoaded || noPlaylists ? (
        <div className="flex h-4/5 w-full  items-center justify-center">
          <div className="flex w-[200px] flex-col items-center text-center">
            <img
              className={cn(isDarkMode && "invert")}
              height={68}
              width={68}
              alt="playlist"
              src={getTabIconFilename({ icon: "icon_playlist.png" })}
            />
            <div className="h4-bold  mt-4">{translate("No playlists")}</div>
            <div className="p1 text-item-secondary mt-2">
              {translate("Looks like you haven't saved anything yet")}
            </div>
          </div>
        </div>
      ) : (
        <div>
          {data?.Playlists?.map((playlist, idx) => {
            const items = itemsByPlaylist[playlist.PlaylistId];

            const dropdownOptions = [
              {
                onClick: () => {
                  deletePlaylist.mutate(
                    {
                      PlaylistId: playlist.PlaylistId,
                    },
                    {
                      onSuccess: () => {
                        toast(
                          `${translate("Deleted")} ${
                            playlist.PlaylistName || translate("Playlist")
                          } `,
                        );
                      },
                    },
                  );
                },
                text: translate("Delete Playlist"),
                icon: <Delete className="h-4 w-4" />,
                color: "error",
              },
              {
                onClick: () => {
                  setUpdatePlaylistId(playlist.PlaylistId);
                  setModalOpen(true);
                },
                icon: <Edit className="h-4 w-4" />,
                text: translate("Rename Playlist"),
              },
            ];

            return (
              <PlaylistSlider
                key={playlist.PlaylistId}
                playlist={playlist}
                dropdownOptions={dropdownOptions}
                idx={idx}
                items={items}
                fullItems={{ videos: videoById, collections: collectionById }}
              />
            );
          })}
        </div>
      )}
      <UpdatePlaylistModal
        key={updatePlaylistId}
        open={isModalOpen}
        setOpen={setModalOpen}
        playlistId={updatePlaylistId}
        existingName={
          data?.Playlists?.find((p) => p.PlaylistId === updatePlaylistId)
            ?.PlaylistName || ""
        }
        onSuccess={() => setUpdatePlaylistId(undefined)}
      />
      <ReorderPlaylistsDrawer />
    </div>
  );
};
